import { GooglePlacesQueryService } from '@supply-mover-2-0/core';

/**
 * PD-16876 — Google bills every map drawn in the browser (a "map load"), and
 * that cost is charged to the tenant's monthly Google credit pool. Each place
 * that builds a `google.maps.Map` calls `recordGoogleMapLoad` right after, and
 * this sends one fire-and-forget report to our server.
 *
 * A map load is never blocked: the map has already been drawn, and the
 * report's success or failure is never shown. Each report carries an id made
 * of this page load and a counter, so a retried report counts once.
 */

/** One id per page load (a module instance lives as long as the page). */
const PAGE_LOAD_ID = createPageLoadId();
let mapCounter = 0;

function createPageLoadId(): string {
	const cryptoApi = (globalThis as { crypto?: { randomUUID?: () => string } })
		.crypto;

	if (cryptoApi?.randomUUID != null) {
		return cryptoApi.randomUUID();
	}

	return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

/** The next map's load id (exposed for tests). */
export function nextMapLoadId(): string {
	mapCounter += 1;

	return `${PAGE_LOAD_ID}-${String(mapCounter)}`;
}

/**
 * Report one map drawn. `source` names the map (e.g. `clients-map`) for the
 * usage breakdown. Never throws, never waits.
 */
export function recordGoogleMapLoad(source: string): void {
	const loadId = nextMapLoadId();

	try {
		void GooglePlacesQueryService.recordMapLoad({ loadId, source }).catch(
			() => undefined,
		);
	} catch {
		// A failed report must never affect the map.
	}
}
